'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { setAttr } from '@directus/visual-editing';
import { useVisualEditing } from '@/hooks/useVisualEditing';
import DirectusImage from '@/components/shared/DirectusImage';
import { Separator } from '@/components/ui/separator';
import Link from 'next/link';
import Headline from '@/components/ui/Headline';
import Container from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import { Post, DirectusUser, PageBlock } from '@/types/directus-schema';
import BaseBlock from '@/components/blocks/BaseBlock';
import { trackEvent } from '@/lib/analytics';

function extractTextStr(obj: any): string {
	if (typeof obj === 'string') return obj;
	if (Array.isArray(obj)) return obj.map(extractTextStr).join(' ');
	if (typeof obj === 'object' && obj !== null) {
		return Object.values(obj).map(extractTextStr).join(' ');
	}

	return '';
}

function calculateReadingTime(blocks: PageBlock[] | null | undefined): number {
	if (!blocks || blocks.length === 0) return 1;
	const text = extractTextStr(blocks);
	const cleanText = text.replace(/<[^>]*>?/gm, ' ');
	const words = cleanText.split(/\s+/).filter((w) => w.length > 0).length;

	return Math.max(1, Math.ceil(words / 200));
}

interface BlogPostClientProps {
	post: Post;
	relatedPosts: Post[];
	author?: DirectusUser | null;
	authorName: string;
	postUrl: string;
}

export default function BlogPostClient({ post, relatedPosts, author, authorName, postUrl }: BlogPostClientProps) {
	const { isVisualEditingEnabled, apply } = useVisualEditing();
	const router = useRouter();
	const blogReadFired = useRef(false);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isVisualEditingEnabled) {
			apply({
				onSaved: () => {
					window.location.reload();
				},
			});
		}
	}, [isVisualEditingEnabled, apply]);

	// Track blog_read when user scrolls past 75% of the article
	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !blogReadFired.current) {
					blogReadFired.current = true;
					trackEvent('blog_read', {
						post_title: post.title,
						post_slug: post.slug || '',
					});
				}
			},
			{ threshold: 0 },
		);

		observer.observe(sentinel);

		return () => observer.disconnect();
	}, [post.title, post.slug]);

	const formattedDate = post.published_at
		? new Intl.DateTimeFormat('nl-NL', {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			}).format(new Date(post.published_at))
		: null;

	return (
		<article className="min-h-screen pb-16">
			{/* --- SimpleHero Style Blog Header --- */}
			<section className="relative pb-2 pt-4 md:pt-6 bg-white">
				<Container>
					{/* Breadcrumb */}
					<nav aria-label="Breadcrumb" className="text-sm text-gray-500 mb-8 md:mb-12">
						<ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
							<li className="whitespace-nowrap">
								<Link href="/" className="hover:text-gray-900 transition-colors">
									Home
								</Link>
							</li>
							<li className="whitespace-nowrap">
								<span className="text-gray-400">/</span>
							</li>
							<li className="whitespace-nowrap">
								<Link href="/advies" className="hover:text-gray-900 transition-colors">
									Advies & Inspiratie
								</Link>
							</li>
							<li className="whitespace-nowrap">
								<span className="text-gray-400">/</span>
							</li>
							<li className="text-gray-900 font-medium truncate max-w-[200px] sm:max-w-none" aria-current="page">
								{post.title}
							</li>
						</ol>
					</nav>

					<div className="max-w-4xl mx-auto space-y-8">
						{/* Hero Content: Title & Meta Info */}
						<div className="space-y-6">
							<Headline
								as="h1"
								headline={post.title}
								className="text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight font-heading font-bold mb-4"
								data-directus={setAttr({
									collection: 'posts',
									item: post.id,
									fields: ['title', 'slug'],
									mode: 'popover',
								})}
							/>

							{post.description && (
								<h2
									className="text-xl md:text-2xl lg:text-3xl text-gray-700 font-medium leading-relaxed mb-8 max-w-4xl"
									data-directus={setAttr({
										collection: 'posts',
										item: post.id,
										fields: 'description',
										mode: 'popover',
									})}
								>
									{post.description}
								</h2>
							)}
						</div>

						{/* Hero Image with Reading Time Label */}
						{post.image && (
							<div
								className="relative w-full overflow-hidden rounded-2xl mt-8"
								data-directus={setAttr({
									collection: 'posts',
									item: post.id,
									fields: ['image', 'meta_header_image'],
									mode: 'modal',
								})}
							>
								<div className="relative w-full h-[300px] md:h-[400px] bg-gray-100">
									<DirectusImage
										uuid={post.image as string}
										alt={post.title || 'Header afbeelding'}
										className="object-cover size-full"
										fill
										priority
										sizes="(max-width: 1024px) 100vw, 1024px"
									/>
									{/* Reading Time Label */}
									<div className="absolute top-4 left-4 lg:top-6 lg:left-6 z-10">
										<div className="bg-white/90 backdrop-blur-sm text-gray-900 font-medium px-4 py-2 rounded-full shadow-sm text-sm flex items-center gap-2">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="lucide lucide-clock"
											>
												<circle cx="12" cy="12" r="10" />
												<polyline points="12 6 12 12 16 14" />
											</svg>
											<span>{calculateReadingTime(post.blocks)} min leestijd</span>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</Container>
			</section>

			{/* --- Dynamic Blocks --- */}
			<div
				className="w-full flex flex-col"
				data-directus={setAttr({
					collection: 'posts',
					item: post.id,
					fields: ['blocks'],
					mode: 'drawer',
				})}
			>
				{post.blocks?.map((block: PageBlock) => (
					<BaseBlock key={block.item.id || block.id} block={block} pageId={post.id} collection="posts" />
				))}
				{/* Sentinel for blog_read tracking — placed at ~75% of content */}
				<div ref={sentinelRef} style={{ position: 'relative', bottom: '25%' }} aria-hidden />
			</div>

			{/* --- Related Posts Footer --- */}
			{relatedPosts && relatedPosts.length > 0 && (
				<section className="bg-background-muted py-16 border-t mt-8">
					<Container>
						<Headline
							as="h2"
							headline="Gerelateerde Artikelen"
							className="text-3xl font-bold mb-10 text-center !text-accent"
						/>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
							{relatedPosts.map((relatedPost) => {
								const rAuthorName = relatedPost.author
									? [relatedPost.author.first_name, relatedPost.author.last_name].filter(Boolean).join(' ')
									: '';

								return (
									<Link key={relatedPost.id} href={`/advies/${relatedPost.slug}`} className="group block h-full">
										<div className="flex flex-col h-full overflow-hidden rounded-xl bg-background border shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1">
											{relatedPost.image ? (
												<div className="relative w-full h-48 overflow-hidden bg-muted">
													<DirectusImage
														uuid={relatedPost.image as string}
														alt={relatedPost.title}
														className="object-cover transition-transform duration-500 group-hover:scale-105"
														fill
														sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
													/>
												</div>
											) : null}
											<div className="p-6 flex flex-col flex-1">
												<h3 className="line-clamp-2 text-xl font-bold mb-3 text-accent group-hover:text-amber-500 transition-colors">
													{relatedPost.title}
												</h3>
												{relatedPost.description && (
													<p className="text-muted-foreground text-sm flex-1 line-clamp-3 mb-4">
														{relatedPost.description}
													</p>
												)}
												<div className="mt-auto pt-4 border-t flex items-center justify-between">
													<span className="text-sm font-medium text-foreground">{rAuthorName}</span>
													<span className="text-sm text-accent font-semibold flex items-center">
														Lees meer <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
													</span>
												</div>
											</div>
										</div>
									</Link>
								);
							})}
						</div>
					</Container>
				</section>
			)}
		</article>
	);
}
